import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';

export interface TenantModuleSettings {
    enabled: boolean;
    limit?: number; // 0 / undefined = unlimited
}

export type TenantModulesMap = Record<string, TenantModuleSettings>;

const useTenantLimits = () => {
    const [settings, setSettings] = useState<TenantModulesMap>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const docRef = doc(db, 'tenantSettings', 'modules');
        const unsubscribe = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
                setSettings(snap.data() as TenantModulesMap);
            }
            setLoading(false);
        }, () => setLoading(false));
        return () => unsubscribe();
    }, []);

    /**
     * Returns true if the module is enabled (or not configured yet — default on)
     */
    const isModuleActive = (key: string): boolean => {
        const s = settings[key];
        if (!s) return true; // not configured → enabled
        if (typeof s === 'boolean') return s as unknown as boolean; // legacy bool format
        return s.enabled !== false;
    };

    /**
     * Returns the configured numeric limit, or 0 (unlimited) if not set.
     */
    const getLimit = (key: string): number => {
        const s = settings[key];
        if (!s || typeof s === 'boolean') return 0;
        return s.limit ?? 0;
    };

    /**
     * Returns true if the current count is AT or ABOVE the limit.
     * If limit is 0 / undefined → always allowed.
     */
    const isLimitReached = (key: string, currentCount: number): boolean => {
        const limit = getLimit(key);
        if (!limit || limit <= 0) return false;
        return currentCount >= limit;
    };

    return { settings, loading, isModuleActive, getLimit, isLimitReached };
};

export default useTenantLimits;
